import { Dialog, Modal as AriaModal, ModalOverlay } from "react-aria-components"
import { twMerge } from "tailwind-merge"
import { Body, ButtonGroup, Footer } from "./common"
import { Header } from "./Header"
import { type ModalProps } from "./Modal"

export type DrawerProps = Omit<ModalProps, "size"> & {
  location?: "bottom" | "left" | "right" | "top"
}

/**
 * Drawer content that prevents interaction with the rest of the page. Useful
 * for mobile alternatives to modals and dropdowns. Requires
 * passing `isOpen` state and `onOpenChange` handler to manage visibility.
 *
 * ```tsx
 * import { Button } from "@tokenterminal/core/ui/button/Button"
 * import { Drawer } from "@tokenterminal/core/ui/overlays/modal/Drawer"
 *
 * <Button onClick={() => setIsOpen(true)}>Open drawer</Button>
 * <Drawer isOpen={isOpen} onOpenChange={setIsOpen}>
 *   Drawer content
 * </Drawer>
 * ```
 */

export const Drawer = ({
  children,
  isDismissable = true,
  hasDividers,
  className,
  isKeyboardDismissDisabled = false,
  location = "bottom",
  ...props
}: DrawerProps) => {
  return (
    <ModalOverlay
      isOpen={props.isOpen}
      isDismissable={isDismissable}
      className="tt-modal-overlay items-end"
      isKeyboardDismissDisabled={isKeyboardDismissDisabled}
      {...props}
    >
      <AriaModal
        isDismissable={isDismissable}
        className={twMerge(
          "tt-modal tt-modal-drawer !rounded-bl-none !rounded-br-none",
          className
        )}
        data-has-dividers={hasDividers}
        data-location={location}
      >
        <Dialog
          className="tt-modal-dialog flex flex-col !h-auto !max-h-[calc(var(--visual-viewport-height)-100px)]"
          data-size="full"
        >
          {children}
        </Dialog>
      </AriaModal>
    </ModalOverlay>
  )
}

Drawer.Header = Header
Drawer.Body = Body
Drawer.ButtonGroup = ButtonGroup
Drawer.Footer = Footer
